import { useState, useEffect } from "react";
import { notification } from "antd";
import { useMetricDropdowns } from "../../../hooks/useDropdownInitialization";
import { useSelector } from "react-redux";
import { sampleOverview, samplePosition } from "./ChartTypes/constants";
import chartTypes, { chartTypeOptions } from "./ChartTypes";
import { trainingAPI } from "@api/TrainingAPI";
import { editorStateToApiPayload, apiResponseToEditorState } from "./chartFormatUtils";

const notifyHandlerError = (label, error) => {
    notification.error({
        message: label,
        description: error?.message || String(error),
        placement: 'top',
        style: { width: '30rem' },
        duration: 6,
    });
};

class ImmutableChartStateManager {
    constructor(positions, chartState) {
        if (!Array.isArray(positions)) {
            throw new Error("positions must be an array");
        }
        this.dummyPosition = {
            'type': 'placeholder',
            'title': 'Add a chart to get started',
        }

        this.minOverviewCharts = 1;
        this.maxOverviewCharts = 50;
        // For all numeric states, charts array must be exactly 4.
        this.posCap = 6;
        this.positions = positions;

        if (chartState === undefined) {
            // Build default overview state.
            let defaultOverview = {
                players: [],
                charts: JSON.parse(JSON.stringify(sampleOverview))
            };
            defaultOverview.charts = defaultOverview.charts.slice(0, this.minOverviewCharts);
            chartState = { overview: defaultOverview };
        }
        // Internally, the state is stored as:
        // { overview: {…}, "state_1": {…}, "state_2": {…}, ... }
        // This ensures that when we rebuild the object, the key order is maintained.
        this.chartState = this._reorderChartState(chartState);
    }

    /**
     * Converts an external state key into an internal representation.
     * External keys are either "overview" or numeric strings like "1", "2", etc.
     * Internal keys store numeric keys as "state_X" (e.g., "1" → "state_1").
     *
     * @param {string} key - The external state key ("overview", "1", "2", …).
     * @returns {string} The corresponding internal key ("overview" or "state_X").
     */
    _internalKey(key) {
        if (key === "overview") return "overview";
        return "state_" + key;
    }

    // Convert an internal key back to its external form.

    /**
     * Converts an internal key ("overview", "state_1", "state_2", …) back to its external form.
     * 
     * @param {string} internalKey - The internal key to be converted.
     * @returns {string} The external key format ("overview" or a numeric string like "1", "2", …).
     */
    _externalKey(internalKey) {
        if (internalKey === "overview") return "overview";
        if (internalKey.startsWith("state_")) {
            return internalKey.substring(6); // remove "state_"
        }
        return internalKey;
    }

    /**
     * Creates a deep clone of the current chart state.
     * This ensures modifications do not mutate the original state.
     *
     * @returns {Object} A new deep-cloned chart state object.
     */
    _cloneChartState() {
        return JSON.parse(JSON.stringify(this.chartState));
    }

    /**
     * Generates a dummy value for a new position entry.
     * This method is used when adding a new position that does not yet have associated charts.
     *
     * @returns {Object} A deep copy of the predefined sample position structure.
     */
    _generateDummyValue() {
        return JSON.parse(JSON.stringify(samplePosition));
    }

    /**
     * Generates an array of dummy charts.
     * Each dummy chart serves as a placeholder with a predefined structure.
     *
     * @returns {Array<Object>} An array of dummy chart objects, each with a placeholder type and title.
     */
    _generateDummyCharts() {
        const dummyCharts = [];
        for (let i = 0; i < this.posCap; i++) {
            dummyCharts.push(this.dummyPosition);
        }
        return dummyCharts;
    }

    /**
     * Reorders the chart state object to maintain a consistent key order.
     * The "overview" key always appears first, followed by numeric state keys in ascending order.
     *
     * @param {Object} state - The existing chart state object.
     * @returns {Object} A new chart state object with keys in the correct order.
     */
    _reorderChartState(state) {
        const ordered = {};
        if (state.hasOwnProperty("overview")) {
            ordered["overview"] = state["overview"];
        }
        const numericKeys = [];
        for (const key in state) {
            if (key === "overview") continue;
            // We expect numeric keys to be stored as "state_X"
            numericKeys.push(key);
        }
        numericKeys.sort((a, b) => {
            const numA = a.startsWith("state_") ? Number(a.substring(6)) : Number(a);
            const numB = b.startsWith("state_") ? Number(b.substring(6)) : Number(b);
            return numA - numB;
        });
        for (const key of numericKeys) {
            ordered[key] = state[key];
        }
        return ordered;
    }

    // --- Public API Methods ---

    /**
     * Validates the integrity of the current chart state.
     * Ensures that:
     * - The "overview" key exists and has valid structure.
     * - All charts within the "overview" state respect the min/max count constraints.
     * - Each state (except "overview") has a valid object structure.
     * - Every state (except "overview") has exactly `posCap` charts.
     * - Each state has at least one player assigned.
     * - All assigned positions exist in the predefined positions list.
     * - No position is duplicated across different states.
     *
     * @returns {boolean} True if the chart state is valid, otherwise false.
     */
    validateState() {
        if (!this.chartState.hasOwnProperty("overview")) return false;
        const overview = this.chartState.overview;
        if (typeof overview !== "object" || overview === null) return false;
        if (!Array.isArray(overview.players) || !Array.isArray(overview.charts)) return false;
        if (overview.charts.length < this.minOverviewCharts || overview.charts.length > this.maxOverviewCharts) {
            return false;
        }
        const seenPositions = new Set();
        for (const key in this.chartState) {
            if (key === "overview") continue;
            const stateObj = this.chartState[key];
            if (typeof stateObj !== "object" || stateObj === null) return false;
            if (!Array.isArray(stateObj.players) || !Array.isArray(stateObj.charts)) return false;
            if (stateObj.charts.length !== this.posCap) return false;
            if (stateObj.players.length < 1) return false;
            for (const pos of stateObj.players) {
                if (!this.positions.includes(pos)) return false;
                if (seenPositions.has(pos)) return false;
                seenPositions.add(pos);
            }
        }
        return true;
    }

    /**
     * Returns an array of positions that are not currently assigned to any state key.
     *
     * @returns {Array<string>} An array of unused positions.
     */
    getUnusedPositions() {
        const usedPositions = new Set();
        for (const key in this.chartState) {
            if (key === "overview") continue;
            const players = this.chartState[key].players;
            players.forEach(pos => usedPositions.add(pos));
        }
        return this.positions.filter(pos => !usedPositions.has(pos));
    }

    /**
     * Removes a specified position from a given state key.
     * If the key corresponds to a numeric state (not "overview") and removing the position results
     * in an empty player list, the state is deleted entirely.
     *
     * @param {string} position - The position to be removed.
     * @param {string} key - The external key ("overview", "1", "2", …) from which to remove the position.
     * @returns {ImmutableChartStateManager} A new instance of the chart state manager with the updated state.
     * @throws {Error} If the provided key does not exist in chartState.
     * @throws {Error} If the specified position is not part of the given key.
     */
    removePositionFromKey(position, key) {
        const intKey = this._internalKey(key);
        if (!this.chartState.hasOwnProperty(intKey)) {
            throw new Error(`Key "${key}" does not exist in chartState.`);
        }
        const playersInKey = this.chartState[intKey].players;
        if (!playersInKey.includes(position)) {
            throw new Error(`Position "${position}" is not part of key "${key}".`);
        }
        const newChartState = this._cloneChartState();
        const stateObj = newChartState[intKey];
        stateObj.players = stateObj.players.filter(pos => pos !== position);
        // For numeric keys, if no positions remain, delete the key.
        if (intKey !== "overview" && stateObj.players.length === 0) {
            delete newChartState[intKey];
        } else {
            newChartState[intKey] = stateObj;
        }
        return new ImmutableChartStateManager(this.positions, this._reorderChartState(newChartState));
    }

    /**
     * Adds a new dummy state with placeholder charts for the specified positions.
     * Ensures that the positions are valid and not already in use before adding them.
     *
     * @param {string[]} positionsArray - An array of position identifiers to be added.
     * @returns {ImmutableChartStateManager} A new instance with the updated chart state.
     * @throws {Error} If positionsArray is not an array.
     * @throws {Error} If any position in positionsArray is invalid or already used.
     */
    addDummyValueForPositions(positionsArray) {
        if (!Array.isArray(positionsArray)) {
            throw new Error("positionsArray must be an array");
        }
        positionsArray.forEach(pos => {
            if (!this.positions.includes(pos)) {
                throw new Error(`Position "${pos}" is not a valid position.`);
            }
        });
        const unused = this.getUnusedPositions();
        positionsArray.forEach(pos => {
            if (!unused.includes(pos)) {
                throw new Error(`Position "${pos}" is already used in chartState.`);
            }
        });
        // Determine the next numeric key by scanning the internal keys.
        let next = 1;
        for (const key in this.chartState) {
            if (key === "overview") continue;
            const extKey = this._externalKey(key);
            next = Math.max(next, Number(extKey) + 1);
        }
        const newKey = "state_" + next;
        const newChartState = this._cloneChartState();
        newChartState[newKey] = {
            players: positionsArray,
            charts: this._generateDummyCharts()
        };
        return new ImmutableChartStateManager(this.positions, this._reorderChartState(newChartState));
    }

    /**
     * Adds a position to an existing key in the chartState.
     *
     * @param {string} position - The position to be added.
     * @param {string} key - The external key ("overview", "1", "2", …) where the position should be added.
     * @returns {ImmutableChartStateManager} A new instance of ImmutableChartStateManager with the updated chartState.
     * @throws {Error} If the provided key does not exist in chartState.
     * @throws {Error} If the position is not a valid position.
     * @throws {Error} If the position is already used in another key.
     */
    addPositionToKey(position, key) {
        const intKey = this._internalKey(key);
        if (!this.chartState.hasOwnProperty(intKey)) {
            throw new Error(`Key "${key}" does not exist in chartState.`);
        }
        if (!this.positions.includes(position)) {
            throw new Error(`Position "${position}" is not a valid position.`);
        }
        for (const k in this.chartState) {
            if (k === "overview") continue;
            if (this.chartState[k].players.includes(position)) {
                throw new Error(`Position "${position}" is already used in key "${this._externalKey(k)}".`);
            }
        }
        const newChartState = this._cloneChartState();
        newChartState[intKey].players.push(position);
        return new ImmutableChartStateManager(this.positions, this._reorderChartState(newChartState));
    }

    /**
     * Updates the positions associated with a given state key.
     * If the provided positions array is empty and the key is numeric, the key is deleted.
     * Otherwise, it updates the state key with the new positions while ensuring no duplicate positions exist.
     *
     * @param {string} key - The external key ("overview", "1", "2", …).
     * @param {Array<string>} positionsArray - The array of positions to associate with the key.
     * @returns {ImmutableChartStateManager} A new instance with the updated state.
     * @throws {Error} If the key does not exist, if positionsArray is not an array, 
     *                 or if any position is already assigned to another key.
     */
    updateKeyWithPositions(positionsArray, key) {
        const intKey = this._internalKey(key);
        if (!this.chartState.hasOwnProperty(intKey)) {
            throw new Error(`Key "${key}" does not exist in chartState.`);
        }
        if (!Array.isArray(positionsArray)) {
            throw new Error("positionsArray must be an array");
        }
        // For numeric keys, if positions array is empty, delete the key.
        if (intKey !== "overview" && positionsArray.length === 0) {
            const newChartState = {};
            for (const k in this.chartState) {
                if (k !== intKey) {
                    newChartState[k] = this.chartState[k];
                }
            }
            return new ImmutableChartStateManager(this.positions, this._reorderChartState(newChartState));
        }
        positionsArray.forEach(pos => {
            if (!this.positions.includes(pos)) {
                throw new Error(`Position "${pos}" is not a valid position.`);
            }
        });
        const currentPlayers = this.chartState[intKey].players;
        for (const pos of positionsArray) {
            if (!currentPlayers.includes(pos)) {
                for (const otherKey in this.chartState) {
                    if (otherKey === "overview" || otherKey === intKey) continue;
                    if (this.chartState[otherKey].players.includes(pos)) {
                        throw new Error(`Position "${pos}" is already used in key "${this._externalKey(otherKey)}".`);
                    }
                }
            }
        }
        const newChartState = this._cloneChartState();
        newChartState[intKey].players = positionsArray;
        return new ImmutableChartStateManager(this.positions, this._reorderChartState(newChartState));
    }

    /**
     * Returns an array of all state keys in the correct order.
     * The order is: "overview" first, followed by numeric state keys in ascending order.
     *
     * @returns {string[]} An array of state keys in sorted order.
     */
    getAllStateKeys() {
        const keys = [];
        if (this.chartState.hasOwnProperty("overview")) {
            keys.push("overview");
        }
        const numericKeys = [];
        for (const key in this.chartState) {
            if (key === "overview") continue;
            numericKeys.push(Number(this._externalKey(key)));
        }
        numericKeys.sort((a, b) => a - b);
        const extNumericKeys = numericKeys.map(String);
        return ["overview", ...extNumericKeys];
    }

    /**
     * Retrieves all charts associated with a given state key.
     *
     * @param {string} stateKey - The external key representing the state ("overview", "1", "2", ...).
     * @returns {Array} A deep-cloned array of charts for the specified state.
     * @throws {Error} If the provided stateKey does not exist.
     */
    getAllStateCharts(stateKey) {
        const intKey = this._internalKey(stateKey);
        return JSON.parse(JSON.stringify(this.chartState?.[intKey]?.charts));
    }

    /**
     * Retrieves the list of positions associated with the specified state key.
     *
     * @param {string} stateKey - The external key ("overview", "1", "2", …).
     * @returns {Array} An array of positions assigned to the given state key.
     * @throws {Error} If the provided stateKey does not exist.
     */
    getPositions(stateKey) {
        const intKey = this._internalKey(stateKey);
        return [...this.chartState?.[intKey]?.players];
    }

    /**
     * Retrieves a specific chart at the given index within a specified state key.
     * The returned object is a deep copy to ensure immutability.
     *
     * @param {string} stateKey - The external key ("overview", "1", "2", …).
     * @param {number} index - The index of the chart within the state's chart array.
     * @returns {object} A deep copy of the requested chart object.
     * @throws {Error} If the provided stateKey does not exist.
     * @throws {Error} If the index is out of bounds.
     */
    getStateCharts(stateKey, index) {
        const intKey = this._internalKey(stateKey);
        return JSON.parse(JSON.stringify(this.chartState?.[intKey]?.charts[index]));
    }

    /**
     * Returns a deep copy of the entire chart state.
     * This ensures that the original state remains immutable and prevents direct modifications.
     *
     * @returns {Object} A deep copy of the current chart state.
     */
    getState() {
        return JSON.parse(JSON.stringify(this.chartState));
    }

    /**
     * Updates a chart at a specific index within a given state key.
     * This method returns a new ImmutableChartStateManager instance with the updated state.
     *
     * @param {string} key - The external key ("overview", "1", "2", …) identifying the state.
     * @param {number} index - The index of the chart within the specified state.
     * @param {object} chart - The new chart object to replace the existing chart at the given index.
     * @returns {ImmutableChartStateManager} A new instance with the updated chart state.
     * @throws {Error} If the provided key does not exist in chartState.
     * @throws {Error} If the index is out of bounds for the charts array.
     */
    updateChartAtKeyIndex(key, index, chart) {
        const intKey = this._internalKey(key);
        const newChartState = this._cloneChartState();
        newChartState[intKey].charts[index] = chart;
        return new ImmutableChartStateManager(this.positions, this._reorderChartState(newChartState));
    }

    /**
    * Returns an array of positions that are currently used in the chart state.
    * A position is considered "used" if it appears in the `players` array of any state key (except "overview").
    *
    * @returns {string[]} An array of unique positions that are currently assigned to a state.
    */
    getUsedPositions() {
        const usedPositions = new Set();
        for (const key in this.chartState) {
            if (key === "overview") continue;
            this.chartState[key].players.forEach(pos => usedPositions.add(pos));
        }
        return [...Array.from(usedPositions)];
    }

    /**
     * Adds a new placeholder chart to the overview section.
     *
     * @returns {ImmutableChartStateManager} A new instance of `ImmutableChartStateManager` with the updated state.
     * @throws {Error} If adding another chart would exceed the maximum allowed charts.
     */
    addChartToOverview() {
        const newChartState = this._cloneChartState();
        const overview = newChartState["overview"];
        if (overview.charts.length >= this.maxOverviewCharts) {
            throw new Error(`Overview charts cannot exceed ${this.maxOverviewCharts}.`);
        }
        overview.charts.push(this.dummyPosition);
        newChartState["overview"] = overview;
        return new ImmutableChartStateManager(this.positions, this._reorderChartState(newChartState));
    }

    /**
     * Removes a chart from the "overview" state at the specified index.
     * Throws an error if:
     *  - Removing a chart would result in fewer than the minimum allowed overview charts.
     *  - The provided index is out of bounds of the overview charts array.
     *
     * @param {number} index - The index of the chart to be removed from the overview.
     * @returns {ImmutableChartStateManager} A new instance of ImmutableChartStateManager with the updated chart state.
     * @throws {Error} If the overview charts count would fall below the minimum, or if the index is invalid.
     */
    removeChartFromOverview(index) {
        const newChartState = this._cloneChartState();
        const overview = newChartState["overview"];
        if (overview.charts.length <= this.minOverviewCharts) {
            throw new Error(`Overview charts cannot be less than ${this.minOverviewCharts}.`);
        }
        if (index < 0 || index >= overview.charts.length) {
            throw new Error("Invalid index for overview charts.");
        }
        overview.charts.splice(index, 1);
        newChartState["overview"] = overview;
        return new ImmutableChartStateManager(this.positions, this._reorderChartState(newChartState));
    }

    /**
     * Reorders charts within a state key by moving the chart at `fromIndex` to `toIndex`.
     *
     * @param {string} stateKey - The external key ("overview", "1", "2", …).
     * @param {number} fromIndex - The current index of the chart to move.
     * @param {number} toIndex - The destination index.
     * @returns {ImmutableChartStateManager} A new instance with the reordered charts.
     * @throws {Error} If the stateKey is missing or either index is out of range.
     */
    reorderCharts(stateKey, fromIndex, toIndex) {
        const intKey = this._internalKey(stateKey);
        if (!this.chartState.hasOwnProperty(intKey)) {
            throw new Error(`Key "${stateKey}" does not exist in chartState.`);
        }
        const newChartState = this._cloneChartState();
        const charts = newChartState[intKey].charts;
        if (fromIndex < 0 || fromIndex >= charts.length || toIndex < 0 || toIndex >= charts.length) {
            throw new Error("Invalid index for chart reorder.");
        }
        if (fromIndex === toIndex) {
            return this;
        }
        const [moved] = charts.splice(fromIndex, 1);
        charts.splice(toIndex, 0, moved);
        return new ImmutableChartStateManager(this.positions, this._reorderChartState(newChartState));
    }

    /**
    * Returns the number of charts in the specified state key that are incomplete.
    * A chart is considered incomplete if any of its fields is the empty string.
    *
    * @param {string} stateKey - The external key ("overview", "1", "2", …).
    * @returns {number} The count of incomplete charts.
    * @throws {Error} If the provided stateKey does not exist.
    */
    countIncompleteCharts(stateKey) {
        const intKey = this._internalKey(stateKey);
        if (!this.chartState.hasOwnProperty(intKey)) {
            throw new Error(`State key "${stateKey}" does not exist in chartState.`);
        }
        const charts = this.chartState[intKey].charts;
        // Count a chart as incomplete if any field is an empty string.
        return charts.filter(chart => {
            return !(Object.entries(chart).some(([key, value]) => value === "" && !key.startsWith('subtitle')) || chart['type'] === 'placeholder')
        }
        ).length;
    }

    /**
     * Performs final validation on the entire chart state.
     * It checks that:
     * - The overall chart configuration is valid (using validateState).
     * - Every chart in every section is complete (no missing fields or placeholder charts).
     *
     * @returns {string} A user-friendly error message if a validation issue is found; otherwise, an empty string.
     */
    finalValidation() {
        // Check overall chart configuration
        if (!this.validateState()) {
            return "The chart configuration is invalid. Please review your selections and ensure all required charts are set up correctly.";
        }
        // Iterate through each section of charts
        const stateKeys = this.getAllStateKeys();
        for (const key of stateKeys) {
            const charts = this.getAllStateCharts(key);
            for (const chart of charts) {
                // A chart is incomplete if it is a placeholder or any field is missing.
                if (chart.type === 'placeholder' || Object.entries(chart).some(([key, value]) => value === "" && !key.startsWith('subtitle'))) {
                    return "One or more charts appear to be incomplete. Please ensure every chart is fully configured with no missing information.";
                }
            }
        }
        return "";
    }

}




export const useChartsManager = (enabled) => {
    const [editingChart, setEditingChart] = useState(null)
    const [editorMetrics, setEditorMetrics] = useState(null)
    const {
        positions = [],
        dropdowns: { gps: gpsDropdowns },
        metrics: reduxMetrics,
    } = useSelector(({ team }) => team);
    const fullMetrics = editorMetrics || reduxMetrics;
    const [chartManager, setChartManager] = useState(() => new ImmutableChartStateManager(positions));
    useMetricDropdowns(['gps']);



    // Rebuild the manager when positions change, but preserve any existing chart state
    // (otherwise an async positions hydration after API load would wipe loaded charts).
    useEffect(() => {
        setChartManager(prev => new ImmutableChartStateManager(positions, prev.getState()))
    }, [positions])

    // Retrieves the human-readable name for a given metric key, falling back to the key if the metric isn't found.
    const getMetricName = (metricKey) => {
        const metric = fullMetrics.find(metric => metric.key === metricKey)
        if (!metric) {
            return metricKey
        }
        return metric?.name
    }
    
    // Adds a new dummy chart state for the specified combination of positions and updates the chart manager accordingly.
    const handleAddSubpositions = (subpositions) => {
        try {
            const newManager = chartManager.addDummyValueForPositions(subpositions);
            setEditingChart(null)
            setChartManager(newManager);
        } catch (e) {
            notifyHandlerError("Could not add positions", e);
        }
    };

    // Removes a specified position from a given state key, updating the chart manager state and clearing any active edits.
    const handleRemovePositionFromKey = (position, key) => {
        try {
            const newManager = chartManager.removePositionFromKey(position, key);
            setEditingChart(null)
            setChartManager(newManager);
        } catch (e) {
            notifyHandlerError("Could not remove position", e);
        }
    };

    /// Adds a specified position to an existing state key within the chart manager.
    const handleAddPositionToKey = (position, key) => {
        try {
            const newManager = chartManager.addPositionToKey(position, key);
            setChartManager(newManager);
        } catch (e) {
            notifyHandlerError("Could not add position", e);
        }
    };

    // Adds a placeholder chart to the overview section.
    const handleAddChartToOverview = () => {
        try {
            const newManager = chartManager.addChartToOverview();
            setChartManager(newManager);
            setTimeout(() => {
                const container = document.querySelector('[data-rbd-droppable-id="charts-overview"]');
                container?.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 0);
        } catch (e) {
            notifyHandlerError("Could not add chart", e);
        }
    };

    // Removes a chart from the overview section at the specified index, clearing any active edits afterward.
    const handleRemoveChartFromOverview = (index) => {
        try {
            const newManager = chartManager.removeChartFromOverview(index);
            setEditingChart(null)
            setChartManager(newManager);
        } catch (e) {
            notifyHandlerError("Could not remove chart", e);
        }
    };

    // Reorders charts within a state key (drag-and-drop). Closes the editor since indices change.
    const handleReorderCharts = (stateKey, fromIndex, toIndex) => {
        try {
            const newManager = chartManager.reorderCharts(stateKey, fromIndex, toIndex);
            setEditingChart(null)
            setChartManager(newManager);
        } catch (e) {
            notifyHandlerError("Could not reorder charts", e);
        }
    };

    // Updates a given state key with a new array of positions, removing the key if the positions array is empty, and clears any active edits.
    const handleUpdateKeyWithPositions = (positions, key) => {
        try {
            const newManager = chartManager.updateKeyWithPositions(positions, key);
            setEditingChart(null)
            setChartManager(newManager);
        } catch (e) {
            notifyHandlerError("Could not update positions", e);
        }
    };

    // Initiates editing mode for a specific chart identified by its key and index, preparing necessary state details for editing.
    const handleEditChart = (key, index) => {
        try {
            const currentValue = chartManager.getStateCharts(key, index)
            const activeChartKeys = Object.keys(chartTypes?.[currentValue?.type]).filter((key) => key !== 'type' && !key.startsWith('_'))
            setEditingChart({
                ver: 0,
                key,
                index,
                currentValue,
                mutableParameters: currentValue?.type === 'placeholder' ? [] : activeChartKeys,
                chartType: chartTypes?.[currentValue?.type]
            })
        } catch (e) {
            notifyHandlerError("Could not open chart editor", e);
        }
    };

    // Updates the currently edited chart’s state with new values, and optionally forces a version update (useful for re-rendering metric-dependent fields).
    const updateEditChart = (key, val, forceVersionUpdate = 0) => {
        try {
            let changes = { [key]: val }
            // Retrieve the current chart's configuration metadata.
            const chartTypeData = editingChart.chartType
            // Retrieve current chart field values before the update.
            const currentValue = editingChart.currentValue
             // Iterate through all current chart fields to identify dependent fields that auto-update.
            for (const innerKey in currentValue) {
                // Check if the current field has a dependency defined in the metadata (keys starting with "__").
                // The dependency is indicated by a value starting with "$$" followed by the triggering field's key.
                if (chartTypeData?.["__" + innerKey] && chartTypeData?.["__" + innerKey] === "$$" + key) {
                    // Find the corresponding metric object based on the newly selected value.
                    const metric = fullMetrics.find(metric => metric.key === val)
                    // Update the dependent field with the human-readable name of the metric, if found.
                    changes[innerKey] = metric?.name

                }
            }
            // Update the editingChart state with the newly computed changes and increment version if forced.
            setEditingChart({
                ...editingChart,
                currentValue: {
                    ...currentValue,
                    ...changes
                },
                ver: editingChart.ver + forceVersionUpdate
            })
        } catch (e) {
            notifyHandlerError("Could not update chart", e);
        }
    };

    // Directly sets the state of the currently edited chart with a new object, without affecting the version counter.
    const updateEditChartDirect = (newObj) => {
        try {
            setEditingChart({
                ...editingChart,
                currentValue: newObj,
                ver: editingChart.ver
            })
        } catch (e) {
            notifyHandlerError("Could not update chart", e);
        }
    };

    /// Switches the currently edited chart to a different chart type, resetting its fields to defaults based on the new type.
    const switchEditChartType = (newChartTypeKey) => {
        // Retrieve the chart configuration object corresponding to the selected chart type key
        const newChartTypeData = chartTypes[newChartTypeKey]
         // Helper function to transform chart type configuration into actual chart object
        const transformChartTypeToChart = (inputObj) => {
            const result = {};
            // Iterate over each property in the input chart type configuration object (The associated configurations are located in the ChartTypes folder)
            for (const key in inputObj) {
                if (!inputObj.hasOwnProperty(key)) continue;
                /// Skip internal metadata keys starting with "_"
                if (key.startsWith('_')) continue;
                // Ensure the value is treated as a string for consistent processing
                let value = "" + inputObj[key];
                // If the value is a string that starts with "$", remove the "$"
                // Handle dynamic fields explicitly (e.g., keys with value '$dynamic' and '#' somewhere in the key name)
                if (value.startsWith('$dynamic')) {
                    // For dynamic fields, create two initial empty entries (e.g., metric_1, metric_2)
                    result[key.replace('#', '1')] = '';
                    result[key.replace('#', '2')] = '';
                } else {
                    // Handle special "$" prefixed fields
                    if (value.startsWith('$')) {
                        // Automatically populate with the predefined default after the "$auto=" prefix
                        if (value.startsWith('$auto=')) {
                            value = value.split('=')[1]
                        } else {
                            // Default to an empty string for other "$" prefixed fields
                            value = '';
                        }
                    }
                    // Set the processed value into the result object
                    result[key] = value;
                }
            }
            return result;
        }
        let currentValue = transformChartTypeToChart(newChartTypeData)
        const activeChartKeys = Object.keys(newChartTypeData).filter((key) => key !== 'type' && !key.startsWith('_'))
        // Use default field values when switching to a new chart type.
        for (const key in currentValue) {
            if (newChartTypeData?.["__" + key] && newChartTypeData["__" + key].startsWith('%')) {
                currentValue[key] = newChartTypeData["__" + key].replace('%', '')
            }
        }
        try {
            setEditingChart(currentEditChartData => (
                {
                    ...currentEditChartData,
                    chartType: newChartTypeData,
                    currentValue: currentValue,
                    mutableParameters: activeChartKeys
                }))
        } catch (e) {
            notifyHandlerError("Could not switch chart type", e);
        }
    };

    // Saves changes made to the currently edited chart, updating the main chart manager state and exiting edit mode.
    const handleSaveEditChart = () => {
        try {
            const newManager = chartManager.updateChartAtKeyIndex(editingChart.key, editingChart.index, editingChart.currentValue);
            setChartManager(newManager);
            setEditingChart(null)
        } catch (e) {
            notifyHandlerError("Could not save chart edits", e);
        }
    };

    // Persists the current chart configuration to the server via an API call.
    const saveCharts = async () => {
        const payload = editorStateToApiPayload(chartManager.getState())
        await trainingAPI.savePlatformCharts(payload)
    }

    // Load remote charts
    useEffect(() => {
        if (!enabled) return
        const loadCharts = async () => {
            try {
                const response = await trainingAPI.getPlatformCharts()
                const editorState = apiResponseToEditorState(response)
                if (editorState && editorState.overview) {
                    setChartManager(new ImmutableChartStateManager(positions, editorState))
                }
                if (response.all_metrics) {
                    setEditorMetrics(response.all_metrics)
                }
            } catch (e) {
                console.error("Failed to load platform charts:", e)
            }
        }
        loadCharts()
    }, [enabled])

    const unusedPositions = chartManager.getUnusedPositions()
    const usedPositions = chartManager.getUsedPositions()
    const stateKeys = chartManager.getAllStateKeys()

    return {
        editingChart,
        metrics: gpsDropdowns,
        fullMetrics,
        usedPositions,
        unusedPositions,
        chartTypes,
        stateKeys,
        chartTypeOptions,
        maxOverviewCharts: chartManager.maxOverviewCharts,
        getPositions: (stateKey) => chartManager.getPositions(stateKey),
        getAllStateCharts: (stateKey) => chartManager.getAllStateCharts(stateKey),
        getStateCharts: (stateKey, index) => chartManager.getStateCharts(stateKey, index),
        countIncompleteCharts: (stateKey) => chartManager.countIncompleteCharts(stateKey),
        finalValidation: () => chartManager.finalValidation(),
        getMetricName,
        switchEditChartType,
        handleSaveEditChart,
        handleAddSubpositions,
        updateEditChart,
        updateEditChartDirect,
        handleRemovePositionFromKey,
        handleAddPositionToKey,
        handleEditChart,
        handleUpdateKeyWithPositions,
        handleAddChartToOverview,
        handleRemoveChartFromOverview,
        handleReorderCharts,
        saveCharts,

    }
}